/**
 * Hook to sync user preferences with the backend API
 * 
 * This hook provides functions to load and save user preferences from/to the backend.
 * It should be used alongside the UserPreferencesProvider for local state management.
 */

import { useCallback } from 'react';
import { getApiBaseUrl } from '@/lib/data-service';
import { authFetch } from '@/lib/auth-fetch';

export interface BackendUserPreferences {
  userId: string;
  featureModes?: string;
  sensitiveDataVisible: boolean;
  theme: string;
  columnPreferences?: string;
  selectedCompanyId?: number | null;
  sidebarPosition: string;
  sidebarStyle: string;
  showWelcomeDialog: boolean;
  defaultLandingPage: string;
  rememberLastPage: boolean;
  lastVisitedPage?: string;

  keyboardShortcutsEnabled: boolean;
  projectCreationWorkflow: string;
  createdAt?: string;
  updatedAt?: string;
}

export function useUserPreferencesSync() {
  const backendUrl = getApiBaseUrl();

  /**
   * Load user preferences from the backend
   */
  const loadPreferences = useCallback(async (): Promise<BackendUserPreferences | null> => {
    try {
      const response = await authFetch(`${backendUrl}/UserPreferences/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to load user preferences from backend:', response.statusText);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error loading user preferences from backend:', error);
      return null;
    }
  }, [backendUrl]);

  /**
   * Save user preferences to the backend
   */
  const savePreferences = useCallback(async (preferences: Partial<BackendUserPreferences>): Promise<boolean> => {
    try {
      const response = await authFetch(`${backendUrl}/UserPreferences/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        console.error('Failed to save user preferences to backend:', response.statusText);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error saving user preferences to backend:', error);
      return false;
    }
  }, [backendUrl]);

  /**
   * Save selected company to the backend (dedicated endpoint for efficiency)
   */
  const saveSelectedCompany = useCallback(async (selectedCompanyId: number | null): Promise<boolean> => {
    try {
      const response = await authFetch(`${backendUrl}/UserPreferences/me/selected-company`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selectedCompanyId }),
      });

      if (!response.ok) {
        console.error('Failed to save selected company to backend:', response.statusText);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error saving selected company to backend:', error);
      return false;
    }
  }, [backendUrl]);

  return {
    loadPreferences,
    savePreferences,
    saveSelectedCompany,
  };
}
